/**
 * HeadlightModalAdapter
 *
 * Pure, framework-free translation between the legacy HeadlightModal option shape
 * (`spawnModal` / `spawnMiniModal` `configOptions`) and the Pict-Section-Modal (PSM)
 * `show()` API. Kept dependency-free (no Pict, no DOM) so it is unit-testable in
 * isolation — mirrors `HeadlightToastAdapter`.
 *
 * Division of labour: this class only DECIDES the PSM option shape and builds HTML
 * strings. The broker (`Headlight-Modal-Service`) owns everything impure — the PSM
 * call, live-DOM append + autofocus + `<style>` injection in `onOpen`, the
 * callback<->promise bridge, the body scroll-lock, and the singleton dismiss.
 *
 * Why the footer is built as HTML `content` (not PSM's `buttons[]`): PSM escapes
 * button labels (`Pict-Modal-Window._escapeHTML`), so FontAwesome icons cannot ride
 * in a `buttons[]` Label, and consumer-supplied footer templates already embed
 * `onclick="_Pict.fable.HeadlightModal.confirmCallback()"`. Emitting a Bulma-button
 * footer into `content` (with `buttons: []` passed to PSM) preserves the icons, the
 * onclick coupling, and the exact look — PSM still owns the shell (overlay, header,
 * ×, Escape, focus, theming). The broker keeps `confirmCallback`/`cancelCallback`
 * alive so those onclick strings resolve unchanged.
 */

// The global the embedded footer onclick strings resolve to — identical to the legacy
// service so consumer-supplied footer templates keep working verbatim.
const SERVICE_GLOBAL = '_Pict.fable.HeadlightModal';

/**
 * The panel-close "MiniModal" look + behaviour, lifted OUT of the service so each
 * calling view passes it explicitly: `spawnMiniModal({ ...MiniModalPreset, message }, ...)`.
 * (Was previously copy-pasted across the headlight_panels views.)
 */
const MiniModalPreset =
{
	modalTopOffset: '65px',
	modalBackgroundColor: 'has-background-primary-80',
	modalTextColor: 'has-text-primary-20',
	confirmColor: 'is-primary',
	cancelColor: 'is-soft is-primary',
};

class HeadlightModalAdapter
{
	/**
	 * Escape HTML special characters for safe embedding of plain-text values.
	 * @param {string} pText
	 * @returns {string}
	 */
	escapeHTML(pText)
	{
		if (typeof pText !== 'string')
		{
			return '';
		}
		return pText
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	/**
	 * True when the value is a live DOM element. Checked structurally (nodeType === 1)
	 * so this stays pure — no `document`/`HTMLElement` reference needed.
	 * @param {*} pValue
	 * @returns {boolean}
	 */
	isElement(pValue)
	{
		return !!(pValue && typeof pValue === 'object' && pValue.nodeType === 1);
	}

	/**
	 * Map a legacy Bulma button color string onto the emphasis the footer button
	 * should carry. De-emphasized markers (`is-soft`/`is-light`) win over `primary`/
	 * `link` so a "soft link" cancel stays neutral; `danger` always wins.
	 * @param {string} pColor
	 * @returns {'danger'|'primary'|null}
	 */
	mapButtonEmphasis(pColor)
	{
		let tmpColor = (typeof pColor === 'string') ? pColor : '';
		if (/(^|\s)(is-)?danger(\s|$)/.test(tmpColor))
		{
			return 'danger';
		}
		if (/(^|\s)is-(soft|light)(\s|$)/.test(tmpColor))
		{
			return null;
		}
		if (/(^|\s)(is-)?(primary|link)(\s|$)/.test(tmpColor))
		{
			return 'primary';
		}
		return null;
	}

	/**
	 * Build a single Bulma footer button whose onclick resolves to the broker's
	 * kept-alive callback method. `pIconClass` is the FontAwesome glyph class WITHOUT
	 * the `fas fa-fw ` prefix (e.g. `fa-check` or `fa-check-circle`); pass null/'' for none.
	 * @param {string} pHash - 'confirm' | 'cancel' (drives the onclick method + button id suffix)
	 * @param {string} pText
	 * @param {string} pIconClass
	 * @param {string} pColorClass - raw legacy Bulma color class(es), applied verbatim to the button
	 * @param {string} [pFooterId] - optional id prefix for the button (legacy parity)
	 * @returns {string}
	 */
	buildButtonHTML(pHash, pText, pIconClass, pColorClass, pFooterId)
	{
		let tmpMethod = (pHash === 'confirm') ? 'confirmCallback' : 'cancelCallback';
		let tmpColor = (typeof pColorClass === 'string') ? pColorClass : '';
		let tmpIdAttr = pFooterId ? ' id="' + this.escapeHTML(pFooterId) + '-Button-' + (pHash === 'confirm' ? 'Confirm' : 'Cancel') + '"' : '';
		let tmpIconHTML = '';
		if (pIconClass)
		{
			// Normalize: accept 'fa-check' or bare 'check' (legacy confirmIcon default is 'check').
			let tmpGlyph = (/^fa-/.test(pIconClass)) ? pIconClass : ('fa-' + pIconClass);
			tmpIconHTML = '<span class="icon"><i class="fas fa-fw ' + this.escapeHTML(tmpGlyph) + '"></i></span>';
		}
		return '<button type="button"' + tmpIdAttr + ' class="button ' + this.escapeHTML(tmpColor) + '"' +
			' onclick="' + SERVICE_GLOBAL + '.' + tmpMethod + '()">' +
			tmpIconHTML +
			'<span> ' + this.escapeHTML(pText) + ' </span>' +
			'</button>';
	}

	/**
	 * Build the default confirm/cancel footer buttons (cancel then confirm). Returned WITHOUT
	 * a layout wrapper — the broker drops these into a real `.pict-modal-dialog-footer` band
	 * (PSM styles it as a flex/justify-end/border-top row that sits below the scrollable body),
	 * so the legacy `.columns` negative margins that used to pull the footer up into the body
	 * are gone.
	 * @param {object} pConfig - merged config (confirmText/Icon/Color, cancelText/Icon/Color, modalFooterId)
	 * @returns {string}
	 */
	buildDefaultFooterHTML(pConfig)
	{
		let tmpCancel = this.buildButtonHTML('cancel', pConfig.cancelText, pConfig.cancelIcon, pConfig.cancelColor, pConfig.modalFooterId);
		let tmpConfirm = this.buildButtonHTML('confirm', pConfig.confirmText, pConfig.confirmIcon, pConfig.confirmColor, pConfig.modalFooterId);
		return tmpCancel + tmpConfirm;
	}

	/**
	 * Build the default message body (legacy: a div at font-size 1.3rem).
	 * @param {string} pMessage
	 * @returns {string}
	 */
	buildMessageBodyHTML(pMessage)
	{
		return '<div style="font-size:1.3rem">' + this.escapeHTML(pMessage) + '</div>';
	}

	/**
	 * Build the mini-modal body: bold message + small italic subtext.
	 * @param {string} pMessage
	 * @param {string} pSubtext
	 * @param {string} [pTextColor] - optional Bulma text-color helper class
	 * @returns {string}
	 */
	buildMiniBodyHTML(pMessage, pSubtext, pTextColor)
	{
		let tmpColor = pTextColor ? (' ' + this.escapeHTML(pTextColor)) : '';
		let tmpStrongColor = pTextColor ? (' class="' + this.escapeHTML(pTextColor) + '"') : '';
		return '<p><strong' + tmpStrongColor + '>' + this.escapeHTML(pMessage) + '</strong></p>' +
			'<p class="is-size-7' + tmpColor + '"><em>' + this.escapeHTML(pSubtext) + '</em></p>';
	}

	/**
	 * Build the mini-modal footer (legacy: `.buttons.is-right.mt-2`, cancel then confirm).
	 * @param {object} pConfig
	 * @returns {string}
	 */
	buildMiniFooterHTML(pConfig)
	{
		let tmpCancel = this.buildButtonHTML('cancel', pConfig.cancelText, pConfig.cancelIcon, pConfig.cancelColor);
		let tmpConfirm = this.buildButtonHTML('confirm', pConfig.confirmText, pConfig.confirmIcon, pConfig.confirmColor);
		return '<div class="buttons is-right mt-2">' + tmpCancel + tmpConfirm + '</div>';
	}

	/**
	 * Map the legacy `style` to a PSM dialog width + wide className.
	 * @param {string} pStyle - 'small' | 'wide'
	 * @returns {{ width: (string|undefined), wideClass: (string|null) }}
	 */
	mapStyle(pStyle)
	{
		if (pStyle === 'wide')
		{
			return { width: undefined, wideClass: 'headlight-modal-wide' };
		}
		return { width: undefined, wideClass: null };
	}

	/**
	 * Translate a merged legacy config into a PSM `show()` plan the broker executes.
	 *
	 * String templates are assembled into `content` here. Live-HTMLElement templates
	 * are only FLAGGED (bodyElement/footerElement/cardElement/headerElement) — the broker
	 * appends the actual node in `onOpen` (it has the DOM; this class must not).
	 *
	 * @param {object} pConfig - merged config (defaults already applied by the service)
	 * @param {object} [pContext] - { mini: boolean }
	 * @returns {object} plan
	 */
	translate(pConfig, pContext)
	{
		let tmpConfig = pConfig || {};
		let tmpIsMini = !!(pContext && pContext.mini);

		let tmpStyle = this.mapStyle(tmpConfig.style);
		let tmpClassNames = ['headlight-modal'];
		if (tmpStyle.wideClass) { tmpClassNames.push(tmpStyle.wideClass); }
		if (tmpIsMini) { tmpClassNames.push('headlight-mini-modal'); }
		if (tmpConfig.modalBackgroundColor) { tmpClassNames.push(tmpConfig.modalBackgroundColor); }
		if (tmpConfig.modalTextColor) { tmpClassNames.push(tmpConfig.modalTextColor); }

		let tmpPlan =
		{
			primitive: 'show',
			title: '',
			content: '',
			buttons: [],
			// closeable governs BOTH PSM's × and the overlay/Escape dismiss. Legacy
			// `disableBackgroundClickToClose` (and every mini modal) means "must click a
			// button" -> not closeable. Otherwise closeable (background-click parity).
			closeable: !tmpConfig.disableBackgroundClickToClose,
			width: tmpStyle.width,
			className: tmpClassNames.join(' '),
			freezeScroll: !tmpConfig.enableBackgroundScrolling,
			focusFirstInput: true,
			customCSS: tmpConfig.modalCustomCSS || null,
			hideBackground: !!tmpConfig.hideModalBackground,
			topOffset: tmpConfig.modalTopOffset || null,
			// Default (non-mini, non-custom) footer buttons — the broker renders these in a
			// real .pict-modal-dialog-footer band (below the body), not inside the body content.
			footerContent: '',
			// Live-element flags — the broker appends these nodes in onOpen.
			cardElement: this.isElement(tmpConfig.modalCardTemplate),
			headerElement: this.isElement(tmpConfig.modalHeaderTemplate),
			bodyElement: this.isElement(tmpConfig.modalBodyTemplate),
			footerElement: this.isElement(tmpConfig.modalFooterTemplate),
			// Bucket E: a backdrop-less positioned card is really an anchored popover.
			// Flagged so the service (or a later phase) can route it to dropdown().
			isAnchoredCard: !!(tmpConfig.hideModalBackground && tmpConfig.modalCardTemplate),
		};

		// --- Full-card override: the card owns all chrome; PSM header suppressed. ---
		if (tmpConfig.modalCardTemplate)
		{
			tmpPlan.title = '';
			tmpPlan.content = tmpPlan.cardElement ? '' : String(tmpConfig.modalCardTemplate);
			return tmpPlan;
		}

		let tmpContentParts = [];

		// --- Header: PSM title when default; custom header template goes into content. ---
		if (!tmpConfig.hideModalHeader)
		{
			if (tmpConfig.modalHeaderTemplate)
			{
				if (!tmpPlan.headerElement) { tmpContentParts.push(String(tmpConfig.modalHeaderTemplate)); }
			}
			else
			{
				tmpPlan.title = (typeof tmpConfig.title === 'string') ? tmpConfig.title : '';
			}
		}

		// --- Body ---
		if (!tmpConfig.hideModalBody)
		{
			if (tmpConfig.modalBodyTemplate)
			{
				if (!tmpPlan.bodyElement) { tmpContentParts.push(String(tmpConfig.modalBodyTemplate)); }
			}
			else if (tmpIsMini)
			{
				tmpContentParts.push(this.buildMiniBodyHTML(tmpConfig.message, tmpConfig.subtext, tmpConfig.modalTextColor));
			}
			else
			{
				tmpContentParts.push(this.buildMessageBodyHTML(tmpConfig.message));
			}
		}

		// --- Footer ---
		// Custom footer templates and the mini footer stay IN the body content (the mini's
		// footer shares the colored card; custom footers are the caller's own markup). The
		// DEFAULT footer goes into footerContent so the broker renders it as a proper band.
		if (!tmpConfig.hideModalFooter)
		{
			if (tmpConfig.modalFooterTemplate)
			{
				if (!tmpPlan.footerElement) { tmpContentParts.push(String(tmpConfig.modalFooterTemplate)); }
			}
			else if (tmpIsMini)
			{
				tmpContentParts.push(this.buildMiniFooterHTML(tmpConfig));
			}
			else
			{
				tmpPlan.footerContent = this.buildDefaultFooterHTML(tmpConfig);
			}
		}

		tmpPlan.content = tmpContentParts.join('');
		return tmpPlan;
	}
}

module.exports = HeadlightModalAdapter;
module.exports.MiniModalPreset = MiniModalPreset;
module.exports.SERVICE_GLOBAL = SERVICE_GLOBAL;
